import { useEffect, useId, useMemo, useRef, useState } from "react";
import arrowIcon from "../../../src/assets/arrowIcon.svg";

export default function CustomSelect({
                                         value,
                                         onChange,
                                         options = [],
                                         placeholder = "",
                                         id,
                                         name,
                                         disabled = false,
                                     }) {
    const autoId = useId();
    const listboxId = `${id || autoId}-listbox`;

    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(() => {
        const idx = options.findIndex(o => o.key === value);
        return idx >= 0 ? idx : 0;
    });

    const selected = useMemo(
        () => options.find(o => o.key === value),
        [options, value]
    );

    /* keep activeIndex aligned with current value */
    useEffect(() => {
        const idx = options.findIndex(o => o.key === value);
        if (idx >= 0) setActiveIndex(idx);
    }, [value, options]);

    /* close on outside click */
    useEffect(() => {
        const onDocDown = (e) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, []);

    const setAndClose = (idx) => {
        const opt = options[idx];
        if (!opt) return;
        onChange?.(opt.key);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (disabled) return;

        const max = options.length - 1;

        if (!open) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
                const idx = options.findIndex(o => o.key === value);
                setActiveIndex(idx >= 0 ? idx : 0);
            }
            return;
        }

        switch (e.key) {
            case "Escape":
                e.preventDefault();
                setOpen(false);
                break;
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex(i => Math.min(i + 1, max));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex(i => Math.max(i - 1, 0));
                break;
            case "Home":
                e.preventDefault();
                setActiveIndex(0);
                break;
            case "End":
                e.preventDefault();
                setActiveIndex(max);
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                setAndClose(activeIndex);
                break;
            case "Tab":
                setOpen(false);
                break;
            default:
                break;
        }
    };

    const activeOptionId = `${listboxId}-opt-${activeIndex}`;

    return (
        <div
            ref={rootRef}
            className={[
                "cs",
                open ? "cs--open" : "",
                selected ? "cs--selected" : "cs--unselected",
                disabled ? "cs--disabled" : "",
            ].join(" ")}
        >
            {name && <input type="hidden" name={name} value={value ?? ""} />}

            <button
                type="button"
                className="cs__control"
                disabled={disabled}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={open ? activeOptionId : undefined}
                onKeyDown={onKeyDown}
                onClick={() => !disabled && setOpen(o => !o)}
            >
        <span className="cs__text">
          {!selected ? (
              <span className="cs__placeholder">{placeholder}</span>
          ) : (
              <span className="cs__value">{selected.label}</span>
          )}
        </span>
                <span className="cs__icon" aria-hidden="true">
          <img
              src={arrowIcon}
              alt=""
              style={{
                  width: 16,
                  height: 16,
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 150ms ease",
              }}
          />
        </span>
            </button>

            {open && (
                <ul
                    id={listboxId}
                    className="cs__menu"
                    role="listbox"
                    tabIndex={-1}
                >
                    {options.map((opt, idx) => {
                        const isSelected = opt.key === value;
                        const isActive = idx === activeIndex;

                        return (
                            <li
                                key={opt.key}
                                id={`${listboxId}-opt-${idx}`}
                                role="option"
                                aria-selected={isSelected}
                                className={[
                                    "cs__option",
                                    isSelected ? "is-selected" : "",
                                    isActive ? "is-active" : "",
                                ].join(" ")}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setAndClose(idx)}
                            >
                                {opt.label}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}